import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, History, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

const MENU_LABELS = {
    dashboard: "Kontrol Paneli", clients: "Müşteriler", projects: "Projeler",
    tasks: "Görevler", "content-calendar": "İçerik Takvimi",
    "shoot-planner": "Görsel Çekim Planlayıcı", finance: "Maliye",
    expenses: "Gider Yönetimi", proposals: "Teklifler", contracts: "Sözleşmeler",
    reports: "Raporlar", employees: "Personeller", departments: "Departmanlar",
    "team-chat": "Personel Sohbeti", "ai-assistant": "AI Asistan", settings: "Ayarlar",
};
const DEFAULT_KEYS = Object.keys(MENU_LABELS);

const FINANCE_RESTRICTED = ["finance"]; // Only admins/finance can enable these — server also enforces access

export default function MenuAccessTab({ userId }) {
    const { user: me } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [target, setTarget] = useState(null);
    const [access, setAccess] = useState(null); // null = default; array = explicit
    const [audit, setAudit] = useState([]);
    const [reason, setReason] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [ma, au] = await Promise.all([
                api.get(`/personnel-authz/${userId}/menu-access`),
                api.get(`/personnel-authz/${userId}/audit`).catch(() => ({ data: { items: [] } })),
            ]);
            setTarget(ma.data);
            setAccess(ma.data.menu_access); // null or array
            setAudit(au.data.items || []);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

    const isExplicit = Array.isArray(access);
    const isChecked = (k) => isExplicit ? access.includes(k) : false;

    const toggle = (k) => {
        setAccess((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            if (list.includes(k)) return list.filter((x) => x !== k);
            return [...list, k];
        });
    };

    const useDefault = () => setAccess(null);
    const selectAll = () => setAccess([...DEFAULT_KEYS]);
    const clearAll = () => setAccess([]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/personnel-authz/${userId}/menu-access`, {
                menu_access: isExplicit ? access : null,
                reason: reason || undefined,
            });
            toast.success("Yetkiler güncellendi.");
            setReason("");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>;
    if (!target) return null;

    // Detect if current user (me) is trying to edit their own or if it's read-only via server error
    const readOnly = me?.id === userId && (me?.role !== "super_admin" && me?.role !== "admin");

    return (
        <div className="space-y-4">
            <div className="card-elevated p-4">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="h-4 w-4 text-[hsl(var(--brand))]" />
                    <div className="text-[13.5px] font-bold">Menü Erişim Yetkileri</div>
                </div>
                <div className="text-[12.5px] text-muted-foreground mb-3">
                    Bu personelin sol menüde hangi öğeleri görebileceğini belirleyin.
                    {!isExplicit && <span className="ml-1">şu an <b>rolü tarafından belirlenen varsayılan</b> uygulanıyor.</span>}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                    <button onClick={useDefault} disabled={!isExplicit || readOnly} className="text-[12px] font-semibold rounded-lg border border-border px-3 py-1.5 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Rol Varsayılanına Dön</button>
                    <button onClick={selectAll} disabled={readOnly} className="text-[12px] font-semibold rounded-lg border border-border px-3 py-1.5 hover:bg-muted disabled:opacity-40">Tümünü Aç</button>
                    <button onClick={clearAll} disabled={readOnly} className="text-[12px] font-semibold rounded-lg border border-border px-3 py-1.5 hover:bg-muted disabled:opacity-40">Tümünü Kapat</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {DEFAULT_KEYS.map((k) => (
                        <label key={k} className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2 hover:bg-muted/40 cursor-pointer" data-testid={`menu-toggle-${k}`}>
                            <span className="text-[13px] font-semibold">
                                {MENU_LABELS[k]}
                                {FINANCE_RESTRICTED.includes(k) && <span className="ml-1 text-[9.5px] uppercase tracking-wide text-muted-foreground">(Sadece Yönetici)</span>}
                            </span>
                            <Switch checked={isChecked(k)} disabled={!isExplicit || readOnly}
                                    onCheckedChange={() => toggle(k)} />
                        </label>
                    ))}
                </div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                    <input placeholder="Değişiklik gerekçesi (opsiyonel)" value={reason} onChange={(e) => setReason(e.target.value)}
                           className="flex-1 h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" />
                    <button onClick={save} disabled={saving || readOnly} className="btn-brand h-10" data-testid="menu-access-save-btn">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
                    </button>
                </div>
                {!isExplicit && <div className="text-[11.5px] text-muted-foreground mt-2">Yetkileri düzenlemek için "Tümünü Aç" veya "Tümünü Kapat" ile başlayın.</div>}
            </div>

            <div className="card-elevated p-4">
                <div className="flex items-center gap-2 mb-3">
                    <History className="h-4 w-4 text-muted-foreground" />
                    <div className="text-[13.5px] font-bold">Yetki Değişiklik Geçmişi</div>
                </div>
                {audit.length === 0 ? (
                    <div className="text-[12.5px] text-muted-foreground py-6 text-center">Henüz kayıt yok.</div>
                ) : (
                    <div className="space-y-2">
                        {audit.map((a) => (
                            <div key={a.id} className="rounded-xl border border-border p-3 text-[12.5px]" data-testid={`audit-row-${a.id}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="font-semibold">{a.actor_name || "—"} <span className="text-muted-foreground font-normal">({a.actor_role})</span></div>
                                    <div className="text-[11px] text-muted-foreground">{new Date(a.at).toLocaleString("tr-TR")}</div>
                                </div>
                                <div className="text-muted-foreground mt-1">
                                    <div><b className="text-foreground">Önceki:</b> {a.old_access === null ? "varsayılan" : (Array.isArray(a.old_access) ? a.old_access.join(", ") || "boş" : String(a.old_access))}</div>
                                    <div><b className="text-foreground">Yeni:</b> {a.new_access === null ? "varsayılan" : (Array.isArray(a.new_access) ? a.new_access.join(", ") || "boş" : String(a.new_access))}</div>
                                    {a.reason && <div className="italic">“{a.reason}”</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
